import { useState } from 'react';

import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Control, Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetByIdQuery, usePostDownloadMutation } from 'apis/report-templates.api';
import {
  FILTER_CONDITIONS_FOR_NUMBERS,
  FILTER_CONDITIONS_FOR_STRINGS,
  REPORT_TYPES,
} from 'constants/reports-templates';
import { useOpen } from 'hooks/useOpen';
import { DownloadReportPayload, ReportTemplateItem } from 'interfaces/report-templates.interface';
import { CommonError } from 'interfaces/shared.interface';
import { ErrorModal, Input, InputNumberFormat, Modal, RadioInputSet } from 'shared-components';
import ModalControlButtons from 'shared-components/ModalControlButtons';
import { downloadDocument } from 'utils/global';
import { successNotify } from 'utils/notifications';

interface Props {
  handleClose: VoidFunction;
  isOpen: boolean;
  reportId: number;
  reportTemplatesData: ReportTemplateItem;
}
const GenerateTemplateModal = ({ handleClose, isOpen, reportId, reportTemplatesData }: Props) => {
  const { t } = useTranslation();
  const shouldFetch = isOpen && !!reportId;
  type ControlType = Control<Record<string, string>>;

  const { error } = useGetByIdQuery(reportId, {
    skip: !shouldFetch,
  });

  const { control, getValues } = useForm<Record<string, string>>({});

  const [downloadReport, { isLoading: isDownloading, error: downloadError }] = usePostDownloadMutation();

  const { data: errorData } = (error as CommonError) || (downloadError as CommonError) || {};

  const [selectedType, setSelectedType] = useState<number>(1);

  const [isErrorModalOpen, handleOpenErrorModal, handleCloseErrorModal] = useOpen();

  const handleDownload = async () => {
    const filtersSource = reportTemplatesData.filters || [];

    const formValues = getValues();

    const updatedFilters = filtersSource.map((filter) => {
      const fieldName = String(filter.id);
      const formValue = formValues[fieldName];

      return {
        ...filter,
        value: formValue !== undefined && formValue !== '' ? formValue : filter.value,
      };
    });

    const payload: DownloadReportPayload = {
      id: reportTemplatesData.id,
      type: selectedType,
      filters: updatedFilters,
    };

    try {
      const response = await downloadReport(payload).unwrap();
      if (response.result) {
        downloadDocument(response.result.content, response.result.fileName, response.result.fileType);
        successNotify(t('SuccessfullyUploaded'));
        handleClose();
      }
    } catch (e) {
      handleOpenErrorModal();
    }
  };

  return (
    <>
      <Modal
        dataCy="create-tenant-modal"
        isOpen={isOpen}
        isDisableOutsideClick
        onClose={handleClose}
        heading="GenerateReport"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleDownload();
          }}
        >
          <div className="mb-4">
            {reportTemplatesData.filters && (
              <div className="mb-4 mt-10">
                {reportTemplatesData.filters.map((filter) => {
                  const isNumberType = filter.columnType === 3;
                  const conditions = isNumberType ? FILTER_CONDITIONS_FOR_NUMBERS : FILTER_CONDITIONS_FOR_STRINGS;
                  const conditionLabel = conditions.find((c) => c.id === filter.condition)?.label;
                  const fieldName = String(filter.id);

                  return (
                    <div key={filter.id} className="mb-2">
                      <label className="text-black-900 dark:text-neutral-100 block text-md mb-2">
                        {filter.tableName}
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          style={{ fontSize: '90%' }}
                          className="text-black-900 dark:text-neutral-100 mx-1"
                        />
                        {filter.columnName} {conditionLabel}
                      </label>

                      <Controller
                        control={control}
                        name={fieldName}
                        defaultValue={filter.value}
                        render={({ field }) => {
                          return isNumberType ? (
                            <InputNumberFormat
                              {...field}
                              value={field.value}
                              onChange={field.onChange}
                              control={control as ControlType}
                              dataCy="quantity-field-item"
                              inputClassName="w-full text-sm"
                              isShownArrowButtons
                              fixedDecimalLength={0}
                            />
                          ) : (
                            <Input
                              {...field}
                              value={field.value}
                              onChange={field.onChange}
                              type="text"
                              className="!w-full"
                            />
                          );
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            <RadioInputSet
              radioLegend={t('ReportType')}
              name="reportType"
              radioOptions={REPORT_TYPES.map((option: any) => ({
                ...option,
                checked: selectedType === option.value,
              }))}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedType(Number(e.target.value))}
              fieldSetClassName="!flex !flex-col"
              optionsClassName="mb-2"
            />
          </div>
          <ModalControlButtons
            isShownSubmit
            isLoading={isDownloading}
            saveButtonText={t('Generate')}
            closeButtonText={t('Cancel')}
            onClose={handleClose}
          />
        </form>
      </Modal>
      <ErrorModal
        errorMessage={errorData?.error?.message}
        description={errorData?.error?.details}
        isOpen={isErrorModalOpen}
        handleClose={handleCloseErrorModal}
      />
    </>
  );
};

export default GenerateTemplateModal;
